/**
 * API service for the product plane: one function per endpoint.
 */

import {
  CapabilityGrain,
  CapabilityMap,
  Ecosystem,
  EcosystemCentreType,
  Matrix,
  MatrixColumns,
  MatrixRank,
  MatrixRows,
  MatrixSort,
  MissionLevel,
  MissionProvider,
  Missions,
  PartnershipGrain,
  Partnerships,
  SupplyFlow,
  Treemap,
  TreemapMeasure,
} from "../types/analytics";
import {
  ProductFilterName,
  ProductFlatFilterName,
  ProductSearchResponse,
  ProductSort,
  SuggestResponse,
} from "../types/productPlane";
import {
  ChainCardType,
  ChainDirection,
  ClassFold,
  CompositionLinkType,
  FlagRule,
  ValueChain,
} from "../types/valueChain";
import { getApiClient } from "./apiClient";

// The picks of the filters: per filter the values picked, each an id or a
// fixed code as the response lists it (`FilterValue.value`). The values of
// one filter are alternatives; the filters that have values must all hold.
export type ProductFilterPicks = { [Name in ProductFilterName]?: string[] };

// The query parameters that select products, as the search, the
// suggestions and the analytics reads take them
export interface ProductSelectionParams extends ProductFilterPicks {
  // Words to look for; every word must match
  keyword?: string;
  // Ranges of numeric specifications, each as "key:minimum:maximum" with
  // either bound empty ("weight:2:10", "weight::10"); one per key at most
  numeric?: string[];
  // Whether mentions that carry no name are listed and counted
  include_unnamed?: boolean;
}

// The query parameters of the product search, named as the backend names
// them. Every one is optional; an absent one takes the backend's default.
export interface ProductSearchParams extends ProductSelectionParams {
  sort?: ProductSort;
  // Results per page
  limit?: number;
  // Results skipped before the page
  offset?: number;
  // Whether the response carries the filters with their counts
  include_filters?: boolean;
  // Most values returned for one flat filter; the backend's default is 20
  filter_limit?: number;
  // The flat filter a typed fragment shortens; with `narrow_text`
  narrow_filter?: ProductFlatFilterName;
  // The fragment typed into that filter: only its values whose name holds
  // the fragment are returned. It changes neither the results nor another
  // filter.
  narrow_text?: string;
}

/**
 * Search products by keyword and filters, with an order and a page
 *
 * A parameter with several values (the picks of a filter, the numeric
 * ranges) is written as a repeated key, `category=a&category=b`, which is
 * what the backend reads; the client's default would write `category[]=a`.
 */
export const searchProducts = async (
  params: ProductSearchParams,
): Promise<ProductSearchResponse> => {
  const client = await getApiClient();
  const response = await client.get("/graph/product/search/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The query parameters of the suggestions for typed text: the text, and the
// selection the suggestions are counted under
export interface SuggestParams extends ProductSelectionParams {
  // The text as typed
  text: string;
  // Most suggestions of one group; the backend's default is 5
  limit?: number;
}

/**
 * Suggest the filter values and products that text typed into Explore's
 * search names, each with what picking it gives under the selection
 */
export const suggestProductFilters = async (
  params: SuggestParams,
): Promise<SuggestResponse> => {
  const client = await getApiClient();
  const response = await client.get("/graph/product/suggest/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The query parameters of the read of one product
export interface ProductDetailParams {
  // Entries of the source list in the response
  sources_limit?: number;
  // Entries of the source list skipped before them
  sources_offset?: number;
}

/**
 * Get one product: everything its view shows, with a page of its sources
 */
export const getProduct = async (
  productId: string,
  params: ProductDetailParams = {},
): Promise<import("../types/productPlane").ProductDetail> => {
  const client = await getApiClient();
  const response = await client.get(
    `/graph/product/${encodeURIComponent(productId)}/`,
    { params },
  );
  return response.data;
};

// ── One category ────────────────────────────────────────────────────
// The read of one category goes under this line (sp-528c tk-58c6).

// The query parameters of the read of one category: the size and the place of
// the page of each of its lists. An absent one takes the backend's default.
export interface CategoryDetailParams {
  products_limit?: number;
  products_offset?: number;
  narrower_limit?: number;
  narrower_offset?: number;
  // Most claimed capabilities; the backend's default is 10
  capabilities_limit?: number;
  capabilities_offset?: number;
  // Specification ranges; the backend's default is 20
  specifications_limit?: number;
  specifications_offset?: number;
  sources_limit?: number;
  sources_offset?: number;
}

/**
 * Get one category: everything its view shows, with a page of each list
 */
export const getCategory = async (
  categoryId: string,
  params: CategoryDetailParams = {},
): Promise<import("../types/categoryDetail").CategoryDetail> => {
  const client = await getApiClient();
  const response = await client.get(
    `/graph/category/${encodeURIComponent(categoryId)}/`,
    { params },
  );
  return response.data;
};

// The query parameters of one level of the category tree
export interface CategoryTreeParams {
  // The class whose narrower classes are listed; the top of the tree when
  // absent
  category_id?: string;
  // Whether product mentions that carry no name are counted
  include_unnamed?: boolean;
  // Classes on the page; the backend's default is 50, its most 200
  limit?: number;
  // Classes skipped before the page
  offset?: number;
}

/**
 * Get one level of the category tree: the top, or the curated classes
 * directly beneath a class
 */
export const getCategoryTree = async (
  params: CategoryTreeParams = {},
): Promise<import("../types/categoryDetail").CategoryTreeLevel> => {
  const client = await getApiClient();
  const response = await client.get("/graph/category/tree/", { params });
  return response.data;
};

// ── One capability part ─────────────────────────────────────────────
// The read of one capability part goes under this line (sp-528c tk-5fe6).

// The query parameters of the read of one capability part. The four bearer
// groups share one page size and each has an offset of its own, so one
// group is paged further by raising its offset and leaving the others.
export interface CapabilityPartParams {
  // Whether product mentions that carry no name are among the product
  // bearers and behind the counts
  include_unnamed?: boolean;
  // Page size of every bearer group; the backend's default is 20
  bearers_limit?: number;
  // Product bearers skipped
  products_offset?: number;
  // Organization bearers skipped
  organizations_offset?: number;
  // Project bearers skipped
  projects_offset?: number;
  // Category bearers skipped
  categories_offset?: number;
  // Entries of each counted list; the backend's default is 10
  counts_limit?: number;
  // Entries of the source list in the response
  sources_limit?: number;
  // Entries of the source list skipped before them
  sources_offset?: number;
}

/**
 * Get one capability part: what it is claimed with, a page of each group of
 * its bearers, its place among the parts and a page of its sources
 */
export const getCapabilityPart = async (
  partId: string,
  params: CapabilityPartParams = {},
): Promise<import("../types/capabilityPartDetail").CapabilityPartDetail> => {
  const client = await getApiClient();
  const response = await client.get(
    `/graph/capability/part/${encodeURIComponent(partId)}/`,
    { params },
  );
  return response.data;
};

// ── One incident ────────────────────────────────────────────────────
// The read of one incident goes under this line (sp-528c tk-0d24).

// The query parameters of the read of one incident
export interface IncidentDetailParams {
  // Entries of the source list in the response
  sources_limit?: number;
  // Entries of the source list skipped before them
  sources_offset?: number;
}

/**
 * Get one incident: everything its view shows, with a page of its sources
 */
export const getIncident = async (
  incidentId: string,
  params: IncidentDetailParams = {},
): Promise<import("../types/incidentDetail").IncidentDetail> => {
  const client = await getApiClient();
  const response = await client.get(
    `/graph/incident/${encodeURIComponent(incidentId)}/`,
    { params },
  );
  return response.data;
};

// The request of the value chain: its root in the path, the rest as query
// parameters named as the backend names them. An absent one takes the
// backend's default.
export interface ValueChainParams {
  root_type: ChainCardType;
  // A kind by its id on the curated list or by the id of its row, a class by
  // its id
  root_id: string;
  direction?: ChainDirection;
  // Steps read towards the parts
  parts_depth?: number;
  // Steps read towards the hosts, when the direction reads them
  hosts_depth?: number;
  // The link types read as composition; `component` alone when absent
  link_types?: CompositionLinkType[];
  // Whether product mentions that carry no name are read and counted
  include_unnamed?: boolean;
  // Which classes a product stands on: `primary`, one card per product, when
  // absent, or `every`, the card of each class it is filed under
  classes?: ClassFold;
  // Whether the typical-host rows (what a class or a product is typically
  // built into) are read as edges beside the stated pairs
  typical_hosts?: boolean;
  // The country code the makers are split by and the coverage rows flagged
  // against; none, no split and no coverage rows
  home_jurisdiction?: string;
  // When a coverage row is flagged; `none_domestic` when absent
  flag_rule?: FlagRule;
  // Countries trusted beside the home jurisdiction, a bucket of their own
  // and never domestic; a repeated parameter, read only with a home
  // jurisdiction
  trusted_jurisdictions?: string[];
}

/**
 * Read the value chain of a kind or a class
 */
export const getValueChain = async ({
  root_type,
  root_id,
  ...params
}: ValueChainParams): Promise<ValueChain> => {
  const client = await getApiClient();
  const response = await client.get(
    `/graph/value-chain/${encodeURIComponent(root_type)}/${encodeURIComponent(root_id)}/`,
    {
      params,
      // `link_types` is a repeated parameter: link_types=a&link_types=b
      paramsSerializer: { indexes: null },
    },
  );
  return response.data;
};

// The request of the capability map: the selection as the product search
// takes it (the picks, the keyword, the numeric ranges, the unnamed switch),
// then the map's own parameters. An absent one takes the backend's default.
export interface CapabilityMapParams extends ProductFilterPicks {
  keyword?: string;
  numeric?: string[];
  include_unnamed?: boolean;
  // What a node stands for; `core` when absent
  grain?: CapabilityGrain;
  // Products a node or a pair needs not to be thin
  min_support?: number;
  // The similarity a pair needs to be returned
  min_jaccard?: number;
  // Most nodes returned
  top_n?: number;
}

/**
 * Read the capability map of a selection of products
 *
 * Picks and ranges are repeated keys, as the product search takes them.
 */
export const getCapabilityMap = async (
  params: CapabilityMapParams,
): Promise<CapabilityMap> => {
  const client = await getApiClient();
  const response = await client.get("/graph/analytics/capability-map/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The request of the ecosystem: the selection as the product search takes
// it, then the centre and the reach. An absent one takes the backend's
// default.
export interface EcosystemParams extends ProductFilterPicks {
  keyword?: string;
  numeric?: string[];
  include_unnamed?: boolean;
  centre_type?: EcosystemCentreType;
  // For a kind, the class id or the curated id
  centre_id?: string;
  // Rings around the centre: 1 or 2
  depth?: number;
  // Products a node needs not to be thin
  min_support?: number;
  // Most nodes returned per ring
  top_n?: number;
}

/**
 * Read the ecosystem around a centre within a selection of products
 *
 * An unknown centre answers 404.
 */
export const getEcosystem = async (
  params: EcosystemParams,
): Promise<Ecosystem> => {
  const client = await getApiClient();
  const response = await client.get("/graph/analytics/ecosystem/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The request of the supply flow: the selection as the product search takes
// it, then the jurisdictions. An absent one takes the backend's default.
export interface SupplyFlowParams extends ProductFilterPicks {
  keyword?: string;
  numeric?: string[];
  include_unnamed?: boolean;
  // Without one every known country is in the `other` bucket
  home_jurisdiction?: string;
  // Countries trusted beside the home; a repeated parameter
  trusted_jurisdictions?: string[];
  // Products a node needs not to be thin
  min_support?: number;
  // Most makers returned
  top_n?: number;
}

/**
 * Read the supply flow (hosts, kinds, makers, countries) of a selection of
 * products
 */
export const getSupplyFlow = async (
  params: SupplyFlowParams,
): Promise<SupplyFlow> => {
  const client = await getApiClient();
  const response = await client.get("/graph/analytics/supply-flow/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The request of the treemap: the selection as the product search takes it,
// then the measure and the jurisdictions. An absent one takes the backend's
// default.
export interface TreemapParams extends ProductFilterPicks {
  keyword?: string;
  numeric?: string[];
  include_unnamed?: boolean;
  measure?: TreemapMeasure;
  // Without one no class has a domestic share
  home_jurisdiction?: string;
  // Countries trusted beside the home; a repeated parameter
  trusted_jurisdictions?: string[];
  // Products a class needs not to be thin
  min_support?: number;
  // Most gap classes returned per parent
  top_n?: number;
}

/**
 * Read the treemap of the classes of a selection of products
 */
export const getTreemap = async (params: TreemapParams): Promise<Treemap> => {
  const client = await getApiClient();
  const response = await client.get("/graph/analytics/treemap/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The request of the matrix: the selection as the product search takes it,
// then the table asked for and the narrowing of its rows. An absent one takes
// the backend's default.
export interface MatrixParams extends ProductFilterPicks {
  keyword?: string;
  numeric?: string[];
  include_unnamed?: boolean;
  rows?: MatrixRows;
  // Always sent: the backend refuses capability rows without it
  columns?: MatrixColumns;
  // Read with capability columns only
  grain?: CapabilityGrain;
  // Read with category rows only
  rank?: MatrixRank;
  // Keeps the rows whose name holds this text
  find?: string;
  // A column id: keeps the rows said to have the column
  claiming?: string;
  sort?: MatrixSort;
  // A row id: that row first on every page
  pin?: string;
  // Product rows of a page, and the product rows skipped
  limit?: number;
  offset?: number;
  // Most columns returned
  column_limit?: number;
  // The abilities of the capability axis, in their order; a repeated
  // parameter. Absent: the abilities the scope claims most.
  capabilities?: string[];
  // Effect areas whose abilities the most claimed leave out; a repeated
  // parameter, read only without `capabilities`
  skip_areas?: string[];
}

/**
 * Read the matrix of rows against columns over a selection of products
 */
export const getMatrix = async (params: MatrixParams): Promise<Matrix> => {
  const client = await getApiClient();
  const response = await client.get("/graph/analytics/matrix/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The request of the missions view: the selection as the product search
// takes it, then the capability at the centre, its qualifiers and what the
// rings hold. An absent one takes the backend's default.
export interface MissionsParams extends ProductFilterPicks {
  keyword?: string;
  numeric?: string[];
  include_unnamed?: boolean;
  // A capability pick (a mission, an effect or function area, a core, or a
  // capability); absent, the mission the most hosts of the scope claim
  centre?: string;
  // A condition and a mode the centre's claim must state. Named apart from
  // `condition` and `mode`, the scope's own filters in the same request.
  centre_condition?: string;
  centre_mode?: string;
  // The levels the rings hold; a repeated parameter, never empty
  levels?: MissionLevel[];
  // The kinds of provider edge; a repeated parameter, never empty
  providers?: MissionProvider[];
  // Most capabilities, missions and categories returned
  capability_limit?: number;
  mission_limit?: number;
  category_limit?: number;
}

/**
 * Read one capability at the centre of a selection: the missions and the
 * capabilities that go with it and the categories that provide them
 */
export const getMissions = async (params: MissionsParams): Promise<Missions> => {
  const client = await getApiClient();
  const response = await client.get("/graph/analytics/missions/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};

// The request of the partnerships: the selection as the product search
// takes it, then the grain of the clusters and the organization in focus.
// An absent one takes the backend's default.
export interface PartnershipsParams extends ProductFilterPicks {
  keyword?: string;
  numeric?: string[];
  include_unnamed?: boolean;
  grain?: PartnershipGrain;
  // An organization: it and its closest partners are returned in place of
  // the largest clusters
  focus_id?: string;
  // Most groups returned
  top_n?: number;
  // Most clusters returned, from 1 to 100
  clusters?: number;
  // Most groups returned of each cluster, the most strongly tied first
  per_cluster?: number;
  // A cluster id: it comes back with all its groups, not its strongest few.
  // Ids hold for the same scope and grain only.
  expand?: number;
}

/**
 * Read the organizations that work together on a selection's products
 *
 * An unknown organization in focus, or a public body, answers 404.
 */
export const getPartnerships = async (
  params: PartnershipsParams,
): Promise<Partnerships> => {
  const client = await getApiClient();
  const response = await client.get("/graph/analytics/partnerships/", {
    params,
    paramsSerializer: { indexes: null },
  });
  return response.data;
};
